import { Add, Close, Search } from "@mui/icons-material";
import { InputAdornment, IconButton, Menu, MenuItem, TextField, Tooltip } from "@mui/material";
import { cn } from "components/lib/utils";
import { CaseFormContext } from "contexts/CaseFormContext";
import { FormFieldContext } from "contexts/FormFieldContext";
import { FieldValueGroupTag, TagActionOrigin, TagClientActionType, TagView } from "generated/graphql";
import {
  ComponentType,
  Dispatch,
  HTMLProps,
  MouseEvent,
  RefObject,
  SetStateAction,
  useContext,
  useRef,
  useState,
} from "react";
import { useAppDispatch } from "redux/hooks";
import { tagValueGroup } from "redux/slices/clientLens";
import { TagChip } from "./TagChip";
import { DisplayTag } from "components/tags/types";

interface TagMenuItemProps {
  applied?: boolean;
  tag: TagView;
  action: (tag: DisplayTag) => void;
  readOnly: boolean;
}

function TagMenuItem({ applied, tag, action, readOnly }: TagMenuItemProps) {
  const adornment = applied ? <Close className="h-4 w-4" /> : <Add className="h-4 w-4" />;
  return (
    <MenuItem disabled={readOnly} onClick={() => !readOnly && action(tag)} className="py-0 pt-1">
      <TagChip tag={tag} endAdornment={!readOnly && adornment} active={applied} />
    </MenuItem>
  );
}

interface TagDisplayComponentProps extends TagDisplayProps {
  fieldId: number;
  anchorRef: RefObject<HTMLSpanElement>;
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  addTag: (t: DisplayTag) => void;
  removeTag: (t: DisplayTag) => void;
  handleOpen: (e: MouseEvent) => void;
  handleClose: () => void;
  fullWidth?: boolean;
}

function TagMenu({
  availableTags,
  appliedTags,
  anchorRef,
  open,
  addTag,
  removeTag,
  handleClose,
  fieldId,
}: TagDisplayComponentProps) {
  const [available, setAvailable] = useState(availableTags);
  const appliedSet = new Set(appliedTags.map((t) => t.systemName));
  if (!anchorRef?.current) return null;
  return (
    <Menu
      anchorEl={anchorRef.current}
      open={open}
      onClose={handleClose}
      MenuListProps={{ className: "border rounded-lg" }}>
      <TextField
        className="px-4 pt-2"
        placeholder="Search..."
        onChange={(e) => {
          setAvailable(availableTags.filter((t) => t.displayName.toLowerCase().includes(e.target.value.toLowerCase())));
        }}
        onKeyDown={(e) => e.stopPropagation()}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <Search />
            </InputAdornment>
          ),
        }}
      />
      <p className="text-xs font-semibold mt-4 ml-4">Applied</p>
      {appliedSet.size === 0 && <p className="pl-4 py-1 text-xs text-gray-300 italic">None</p>}
      {available
        .filter((t) => appliedSet.has(t.systemName))
        .map((tag) => (
          <TagMenuItem
            readOnly={tag.readOnlyFields.includes(fieldId)}
            applied
            key={`applied-${tag.systemName}`}
            tag={tag}
            action={removeTag}
          />
        ))}
      <p className="text-xs font-semibold mt-4 ml-4">Available</p>
      {available
        .filter((t) => !appliedSet.has(t.systemName))
        .map((tag) => (
          <TagMenuItem
            readOnly={tag.readOnlyFields.includes(fieldId)}
            key={`available-${tag.systemName}`}
            tag={tag}
            action={addTag}
          />
        ))}
      {appliedSet.size === availableTags.length && <p className="pl-4 py-1 text-xs text-gray-300 italic">None</p>}
    </Menu>
  );
}

interface TagDisplayProps extends HTMLProps<HTMLDivElement> {
  appliedTags: FieldValueGroupTag[];
  availableTags: TagView[];
  valueGroupSerialId: number;
  tagSize?: "xs" | undefined;
}

function DenseTagDisplay(props: TagDisplayComponentProps) {
  const {
    anchorRef,
    handleOpen,
    appliedTags,
    availableTags,
    readOnly,
    fullWidth,
    open,
    setOpen,
    addTag,
    removeTag,
    valueGroupSerialId,
    handleClose,
    fieldId,
    ...rest
  } = props;
  return (
    <div className={cn(fullWidth && "w-full")} {...rest}>
      <span className="self-end h-0 w-0" ref={anchorRef}></span>
      <div className="flex items-center" onClick={handleOpen}>
        <div className="grid">
          {appliedTags
            .toSorted((a, b) => a.displayName.localeCompare(b.displayName))
            .slice(0, 3)
            .map((t, idx) => (
              <Tooltip key={idx} title={t.displayName}>
                <span
                  style={{ backgroundColor: t.colorValue }}
                  className={cn(
                    "rounded-full h-3 w-3 col-start-1 row-start-1 border border-white",
                    idx === 1 && "ml-1.5",
                    idx === 2 && "ml-3"
                  )}
                />
              </Tooltip>
            ))}
        </div>
        {appliedTags.length > 3 && (
          <span className="inline-flex items-center" onClick={handleOpen}>
            <Tooltip
              title={appliedTags
                .toSorted((a, b) => a.displayName.localeCompare(b.displayName))
                .slice(3)
                .map((t) => t.displayName)
                .join(", ")}>
              <p className="ml-1 text-xs text-gray-500">+{appliedTags.length - 3}</p>
            </Tooltip>
          </span>
        )}
        {!props.readOnly && appliedTags.length === 0 && (
          <span className="inline-flex items-center" onClick={handleOpen}>
            <IconButton className="p-0" title="Add">
              <Add className="h-4 w-4" />
            </IconButton>
          </span>
        )}
      </div>
      <TagMenu {...props} />
    </div>
  );
}

function FullTagDisplay(props: TagDisplayComponentProps) {
  const {
    anchorRef,
    handleOpen,
    handleClose,
    appliedTags,
    availableTags,
    valueGroupSerialId,
    className,
    open,
    setOpen,
    addTag,
    removeTag,
    fieldId,
    tagSize,
    ...rest
  } = props;
  const handleTagDelete = (t: DisplayTag) => (e: MouseEvent) => {
    e.stopPropagation();
    removeTag(t);
  };
  return (
    <div className={`flex w-full ${className || ""}`} {...rest}>
      <span className="self-end h-0 w-0" ref={anchorRef}></span>
      <div className={cn("flex flex-wrap items-center gap-2", tagSize === "xs" && "gap-1")} onClick={handleOpen}>
        {appliedTags
          .toSorted((a, b) => a.displayName.localeCompare(b.displayName))
          .map((t) => (
            <TagChip
              key={t.systemName}
              active
              tag={t}
              tagSize={tagSize}
              endAdornment={
                <Close
                  className={cn("cursor-pointer", tagSize === "xs" ? "h-3 w-3" : "h-4 w-4")}
                  onClick={handleTagDelete(t)}
                />
              }
            />
          ))}
        <span className="inline-flex items-center cursor-pointer" onClick={handleOpen}>
          <IconButton className="p-0" title="Add">
            <Add className="h-4 w-4" />
          </IconButton>
        </span>
      </div>
      <TagMenu {...props} />
    </div>
  );
}

function TagDisplayBase({
  DisplayComponent,
  ...props
}: TagDisplayProps & { DisplayComponent: ComponentType<TagDisplayComponentProps> }) {
  const anchorRef = useRef<HTMLSpanElement>(null);
  const ffContext = useContext(FormFieldContext);
  const cfContext = useContext(CaseFormContext);
  const dispatch = useAppDispatch();
  const [open, setOpen] = useState(false);
  const { appliedTags, valueGroupSerialId } = props;
  const groupIndex = ffContext.valueGroups.find((vg) => vg.serialId === valueGroupSerialId)?.id;
  const handleOpen = (e: MouseEvent) => {
    if (!props.readOnly) {
      e.stopPropagation();
      setOpen(true);
    }
  };
  const handleClose = () => setOpen(false);

  const addOrRemoveTag = (action: TagClientActionType, tag: DisplayTag) =>
    dispatch(
      tagValueGroup({
        fieldId: ffContext.formField.id,
        formId: ffContext.form.id,
        caseId: cfContext.caseId,
        updates: {
          actionType: action,
          valueGroupSerialId: valueGroupSerialId,
          systemName: tag.systemName,
        },
      })
    )
      .unwrap()
      .then(() => {
        const nextState =
          action === TagClientActionType.Tag
            ? [
                ...appliedTags,
                {
                  systemName: tag.systemName,
                  displayName: tag.displayName,
                  color: tag.color,
                  colorValue: tag.colorValue,
                  taggedAt: new Date().toISOString(),
                  // TODO: get my puid in here
                  taggedBy: "",
                  origin: TagActionOrigin.AppUser, // FIXME more correct to pass this from the backend.
                },
              ]
            : appliedTags.filter((t) => t.systemName !== tag.systemName);
        // so many things use groupIndex as an id in here, we have to send that until it's fixed
        groupIndex !== undefined && ffContext.setValueGroupTags(groupIndex, nextState as FieldValueGroupTag[]);
      });

  const addTag = addOrRemoveTag.bind(null, TagClientActionType.Tag);
  const removeTag = addOrRemoveTag.bind(null, TagClientActionType.Untag);

  return (
    <DisplayComponent
      {...props}
      fieldId={ffContext.formField.id}
      anchorRef={anchorRef}
      open={open}
      setOpen={setOpen}
      handleOpen={handleOpen}
      handleClose={handleClose}
      addTag={addTag}
      removeTag={removeTag}
    />
  );
}

export function TagDisplay(props: TagDisplayProps) {
  return <TagDisplayBase {...props} DisplayComponent={FullTagDisplay} />;
}

function _DenseTagDisplay(props: TagDisplayProps) {
  return <TagDisplayBase {...props} DisplayComponent={DenseTagDisplay} />;
}

TagDisplay.Dense = _DenseTagDisplay;
